import React, { useEffect } from 'react';

import { ErrorCodes } from '@aline/common';
import { link, routes } from '@aline/routes';
import { gql, makeVar } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { Helmet } from 'react-helmet';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useFontsLoaded } from 'src/App';
import { AppLoading } from 'src/AppLoading';
import { getHasErrorCode } from 'src/components/not-found/get-is-not-found';
import { NOT_FOUND_ENTITIES, NotFoundState } from 'src/components/not-found/NotFoundState';
import { initializePdfTron } from 'src/components/viewer/viewer-context';
import { setEditorFullScreen } from 'src/editor/vars';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { useDocumentRelationshipSubscription } from 'src/hooks';
import { getIsUserInDocOwnerOrg, useGetCurrentDoc } from 'src/hooks/document';
import { useFocusedView, useMarkExternalSession } from 'src/layouts/focused-view';
import { getErrorCode } from 'src/store/errors';

export const editorDocumentIdVar = makeVar<string | null>(null);

/**
 * Intentionally fetch the *minimum* data here.
 * Helps avoid flicker when in Editor and navigating between docs in right panel (e.g., when doc is in a WF).
 */
void gql`
  query GetRequesterDocumentAccess($documentId: UUID!) {
    document(documentId: $documentId) {
      __typename
      id
    }
  }
`;

const { toRelativePath: toRelativeEditorPath } = createRelativeRouter(routes.DocumentDetail, {
  nestedRoutes: true,
});

const LazyAlineEditor = React.lazy(async () => {
  await initializePdfTron();
  const { AlineEditor } = await import('src/editor/AlineEditor');
  return { default: AlineEditor };
});

const LazyVersionHistory = React.lazy(async () => {
  await initializePdfTron();
  const { VersionHistory } = await import('src/version-history');
  return { default: VersionHistory };
});

export function Editor(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const fontsLoaded = useFontsLoaded();
  const { documentId } = useDocumentParams();
  const { data, error, previousData } = Apollo.useGetRequesterDocumentAccessQuery({
    variables: { documentId },
  });

  const ownerOrgMembership = useDocOwnerOrgMembership();
  useFocusedView(ownerOrgMembership === 'loading' ? null : ownerOrgMembership !== 'member');
  useMarkExternalSession(ownerOrgMembership === 'outsider');
  // Keeps doc relationships up to date in related documents drawer.
  useDocumentRelationshipSubscription();
  useEffect(() => {
    if (!error) return;
    if (CombinedGraphQLErrors.is(error)) {
      /**
       * Only `UNAUTHORIZED` is ours. `UNAUTHENTICATED` is handled by `errorLink`
       * in apollo-client.ts, which owns the session redirect for every
       * operation; redirecting here too raced it — both await
       * `getLoginRedirectURI` and then assign `window.location.href`.
       */
      error.errors.forEach(graphQLError => {
        if (getErrorCode(graphQLError) !== ErrorCodes.UNAUTHORIZED) return;
        navigate(link.RequestDocAccess({ documentId }));
      });
    }
  }, [error, navigate, documentId]);
  // Using previousData avoids flicker when navigating between docs in editor.
  const requesterDocument = data?.document ?? previousData?.document;

  useEffect(() => {
    function handleEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setEditorFullScreen(false);
    }

    window.document.addEventListener('keydown', handleEscape);

    return (): void => {
      window.document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    editorDocumentIdVar(documentId);
  }, [documentId]);

  /**
   * Checked before the loading gate below: a purged document never resolves, so
   * without this the editor sits on "Checking document access..." forever. It
   * also wins over the `previousData` fallback, which would otherwise keep the
   * previously-open document on screen after navigating to a dead one.
   */
  if (getIsDocumentMissing(error)) return <NotFoundState entity={NOT_FOUND_ENTITIES.document} />;

  // Fonts need to be loaded *before* AlineEditor so buildRenderMode + Canvas works correct.
  if (!requesterDocument || !fontsLoaded)
    return (
      <AppLoading
        waitingText={!requesterDocument ? 'Checking document access...' : 'Loading fonts...'}
      />
    );

  return (
    <>
      <Helmet>
        <title>Editor</title>
      </Helmet>
      <React.Suspense fallback={<AppLoading waitingText="Loading document viewer..." />}>
        <Routes location={location}>
          <Route
            element={<LazyVersionHistory />}
            path={toRelativeEditorPath(routes.DocumentDetailVersionDetail)}
          />
          {/* editorPaths routes - must come after more specific routes */}
          <Route
            element={<LazyAlineEditor />}
            path={toRelativeEditorPath(routes.DocumentDetailWorkflowDetail)}
          />
          <Route element={<LazyAlineEditor />} path={toRelativeEditorPath(routes.DocumentDetail)} />
        </Routes>
      </React.Suspense>
    </>
  );
}

/**
 * Deliberately not the shared `getIsNotFound`. That helper collapses missing and
 * inaccessible into one answer, and this is the one surface that must tell them
 * apart: `UNAUTHORIZED` already redirects to the request-access flow above,
 * which is a better outcome than a dead end.
 *
 * A trashed document is soft-deleted and still resolves, by design — only a
 * purged one reaches here.
 */
function getIsDocumentMissing(error: unknown): boolean {
  return getHasErrorCode(error, [ErrorCodes.NOT_FOUND]);
}

/**
 * Whether the viewer belongs to the document owner's org. `unknown` when the document or the
 * current user failed to load: the viewer is not known to be internal, so the stripped shell
 * stays, but nothing proves they are external either.
 */
function useDocOwnerOrgMembership(): 'loading' | 'member' | 'outsider' | 'unknown' {
  const { document, loading: loadingDocument } = useGetCurrentDoc();
  const { currentUser, loading: loadingUser } = useCurrentUserQuery();
  if ((loadingDocument && !document) || (loadingUser && !currentUser)) return 'loading';
  if (!document || !currentUser) return 'unknown';

  return getIsUserInDocOwnerOrg(currentUser, document) ? 'member' : 'outsider';
}
